import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Progress } from '../../components/ui/progress';
import { 
  Shield, ShieldCheck, ShieldAlert, ShieldX, Eye, EyeOff,
  CheckCircle2, XCircle, AlertTriangle, Info, Sparkles,
  Copy, Zap, Lock, Key, TrendingUp, AlertCircle
} from 'lucide-react';

// Common weak passwords list (subset for demo) - defined outside component
const commonPasswords = [
  'password', '123456', '123456789', 'qwerty', 'abc123', 'password1',
  'Password1', '12345678', '111111', '123123', 'admin', 'letmein',
  'welcome', 'monkey', 'dragon', 'master', 'sunshine', 'princess',
  'football', 'shadow', 'michael', 'superman', 'batman', 'trustno1',
  'P@ssw0rd', 'Password123', 'Qwerty123', 'Welcome1', 'Admin123'
];

export const PasswordStrengthPage = () => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [suggestion, setSuggestion] = useState('');

  // Analyze password strength - wrapped in useCallback
  const analyzePassword = useCallback((pwd) => {
    if (!pwd) {
      setAnalysis(null);
      return;
    }

    const length = pwd.length;
    const hasLower = /[a-z]/.test(pwd);
    const hasUpper = /[A-Z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSymbol = /[^a-zA-Z0-9]/.test(pwd);
    
    // Check for common patterns
    const hasRepeating = /(.)\1{2,}/.test(pwd);
    const hasSequential = /(abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789)/i.test(pwd);
    const hasCommonWord = commonPasswords.some(common => 
      pwd.toLowerCase().includes(common.toLowerCase())
    );
    const isCommonPassword = commonPasswords.includes(pwd) || 
      commonPasswords.includes(pwd.toLowerCase());
    
    // Calculate character set size
    let charsetSize = 0;
    if (hasLower) charsetSize += 26;
    if (hasUpper) charsetSize += 26;
    if (hasNumber) charsetSize += 10;
    if (hasSymbol) charsetSize += 32;
    
    // Calculate entropy
    const entropy = length > 0 ? Math.log2(Math.pow(charsetSize, length)) : 0;
    
    // Calculate strength score (0-100)
    let score = 0;
    
    // Length contribution (up to 40 points)
    if (length >= 16) score += 40;
    else if (length >= 12) score += 30;
    else if (length >= 8) score += 20;
    else if (length >= 6) score += 10;
    
    // Character variety (up to 40 points)
    if (hasLower) score += 10;
    if (hasUpper) score += 10;
    if (hasNumber) score += 10;
    if (hasSymbol) score += 10;
    
    // Entropy bonus (up to 20 points)
    if (entropy > 100) score += 20;
    else if (entropy > 75) score += 15;
    else if (entropy > 50) score += 10;
    else if (entropy > 25) score += 5;
    
    // Penalties
    if (hasRepeating) score -= 15;
    if (hasSequential) score -= 15;
    if (hasCommonWord) score -= 20;
    if (isCommonPassword) score = Math.min(score, 20);
    
    // Clamp score
    score = Math.max(0, Math.min(100, score));
    
    // Determine strength level
    let level, levelText, levelColor, icon, message;
    if (score < 20) {
      level = 'very-weak';
      levelText = 'Very Weak';
      levelColor = 'text-red-500';
      icon = ShieldX;
      message = 'This password is extremely vulnerable. Please create a much stronger password.';
    } else if (score < 40) {
      level = 'weak';
      levelText = 'Weak';
      levelColor = 'text-orange-500';
      icon = ShieldAlert;
      message = 'This password can be cracked easily. Consider adding more complexity.';
    } else if (score < 60) {
      level = 'medium';
      levelText = 'Medium';
      levelColor = 'text-yellow-500';
      icon = Shield;
      message = 'This password is okay but could be stronger. Add more length and variety.';
    } else if (score < 80) {
      level = 'strong';
      levelText = 'Strong';
      levelColor = 'text-blue-500';
      icon = ShieldCheck;
      message = 'Good password! It provides solid protection against most attacks.';
    } else {
      level = 'very-strong';
      levelText = 'Very Strong';
      levelColor = 'text-green-500';
      icon = ShieldCheck;
      message = 'Excellent password! This provides maximum security against attacks.';
    }
    
    // Calculate crack time estimate (simplified)
    const guessesPerSecond = 1e10; // 10 billion guesses per second
    const possibleCombinations = Math.pow(charsetSize, length);
    const secondsToCrack = possibleCombinations / (2 * guessesPerSecond);
    
    let crackTime;
    if (secondsToCrack < 1) crackTime = 'Instantly';
    else if (secondsToCrack < 60) crackTime = `${Math.round(secondsToCrack)} seconds`;
    else if (secondsToCrack < 3600) crackTime = `${Math.round(secondsToCrack / 60)} minutes`;
    else if (secondsToCrack < 86400) crackTime = `${Math.round(secondsToCrack / 3600)} hours`;
    else if (secondsToCrack < 31536000) crackTime = `${Math.round(secondsToCrack / 86400)} days`;
    else if (secondsToCrack < 3153600000) crackTime = `${Math.round(secondsToCrack / 31536000)} years`;
    else crackTime = 'Centuries';
    
    // Generate requirements checklist
    const requirements = [
      { 
        met: length >= 12, 
        label: 'At least 12 characters', 
        points: length >= 12 ? '✓' : '✗'
      },
      { 
        met: hasUpper, 
        label: 'Uppercase letter (A-Z)', 
        points: hasUpper ? '✓' : '✗'
      },
      { 
        met: hasLower, 
        label: 'Lowercase letter (a-z)', 
        points: hasLower ? '✓' : '✗'
      },
      { 
        met: hasNumber, 
        label: 'Number (0-9)', 
        points: hasNumber ? '✓' : '✗'
      },
      { 
        met: hasSymbol, 
        label: 'Symbol (!@#$%)', 
        points: hasSymbol ? '✓' : '✗'
      },
      { 
        met: !hasRepeating, 
        label: 'No repeated characters', 
        points: !hasRepeating ? '✓' : '✗'
      },
      { 
        met: !hasSequential, 
        label: 'No sequential patterns', 
        points: !hasSequential ? '✓' : '✗'
      },
      { 
        met: !isCommonPassword, 
        label: 'Not a common password', 
        points: !isCommonPassword ? '✓' : '✗'
      }
    ];
    
    // Issues found
    const issues = [];
    if (length < 12) issues.push('Password is too short');
    if (!hasUpper) issues.push('Add uppercase letters');
    if (!hasLower) issues.push('Add lowercase letters');
    if (!hasNumber) issues.push('Add numbers');
    if (!hasSymbol) issues.push('Add symbols');
    if (hasRepeating) issues.push('Remove repeated characters');
    if (hasSequential) issues.push('Avoid sequential patterns');
    if (isCommonPassword) issues.push('This is a commonly used password');
    if (hasCommonWord && !isCommonPassword) issues.push('Contains common words');
    
    setAnalysis({
      score,
      level,
      levelText,
      levelColor,
      icon,
      message,
      entropy: entropy.toFixed(1),
      crackTime,
      length,
      hasLower,
      hasUpper,
      hasNumber,
      hasSymbol,
      hasRepeating,
      hasSequential,
      isCommonPassword,
      hasCommonWord,
      requirements,
      issues
    });
  }, []);

  // Generate improved password suggestion
  const generateImprovedPassword = useCallback(() => {
    if (!password || !analysis) return;
    
    let improved = password;
    
    // Add missing character types
    if (!analysis.hasUpper) {
      const pos = Math.floor(Math.random() * improved.length);
      improved = improved.slice(0, pos) + 'A' + improved.slice(pos);
    }
    
    if (!analysis.hasLower) {
      const pos = Math.floor(Math.random() * improved.length);
      improved = improved.slice(0, pos) + 'a' + improved.slice(pos);
    }
    
    if (!analysis.hasNumber) {
      const randomNum = Math.floor(Math.random() * 10);
      improved += randomNum;
    }
    
    if (!analysis.hasSymbol) {
      const symbols = '!@#$%^&*';
      const randomSymbol = symbols[Math.floor(Math.random() * symbols.length)];
      improved += randomSymbol;
    }
    
    // Add length if needed
    while (improved.length < 12) {
      const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
      improved += chars[Math.floor(Math.random() * chars.length)];
    }
    
    // Replace common patterns
    if (analysis.hasRepeating) {
      improved = improved.replace(/(.)\1{2,}/g, (match) => {
        const char = match[0];
        const randomChars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
        return char + randomChars[Math.floor(Math.random() * randomChars.length)] + char;
      });
    }
    
    setSuggestion(improved);
    toast.success('Improved password generated!', {
      description: 'Review the suggestion below'
    });
  }, [password, analysis]);

  // Use suggested password
  const useSuggestion = useCallback(() => {
    setPassword(suggestion);
    setSuggestion('');
    toast.success('Applied improved password');
  }, [suggestion]);

  // Copy to clipboard
  const copyToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy');
    }
  }, []);

  useEffect(() => {
    analyzePassword(password);
  }, [password, analyzePassword]);

  // Strength indicator component
  const StrengthIndicator = () => {
    if (!analysis) return null;
    
    const Icon = analysis.icon;
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon className={`w-8 h-8 ${analysis.levelColor}`} />
            <div>
              <div className={`text-2xl font-bold ${analysis.levelColor}`}>
                {analysis.levelText}
              </div>
              <div className="text-sm text-muted-foreground">
                Strength: {analysis.score}/100
              </div>
            </div>
          </div>
          
          <Badge 
            className={`px-4 py-2 text-base ${
              analysis.level === 'very-strong' ? 'bg-green-500' :
              analysis.level === 'strong' ? 'bg-blue-500' :
              analysis.level === 'medium' ? 'bg-yellow-500' :
              analysis.level === 'weak' ? 'bg-orange-500' :
              'bg-red-500'
            }`}
          >
            {analysis.levelText}
          </Badge>
        </div>
        
        <Progress 
          value={analysis.score} 
          className={`h-3 ${
            analysis.level === 'very-strong' ? '[&>div]:bg-green-500' :
            analysis.level === 'strong' ? '[&>div]:bg-blue-500' :
            analysis.level === 'medium' ? '[&>div]:bg-yellow-500' :
            analysis.level === 'weak' ? '[&>div]:bg-orange-500' :
            '[&>div]:bg-red-500'
          }`}
        />
        
        <p className="text-sm text-muted-foreground">{analysis.message}</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-card dark">
      <div className="container mx-auto px-4 py-12 space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 border-2 border-primary/50 cyber-glow mb-4">
            <Shield className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent cyber-text-glow">
            Password Strength Checker
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Analyze your password security with advanced algorithms. Get instant feedback and actionable improvements.
          </p>
          
          {/* Privacy Badge */}
          <div className="flex items-center justify-center gap-2 text-sm text-primary">
            <ShieldCheck className="w-4 h-4" />
            <span className="font-medium">Local Analysis • No Storage • Complete Privacy</span>
          </div>
        </div>

        <Tabs defaultValue="checker" className="w-full">
          <TabsList className="grid w-full grid-cols-3 lg:w-[700px] mx-auto mb-8 bg-card/50 border border-border">
            <TabsTrigger 
              value="checker" 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="tab-checker"
            >
              <Shield className="w-4 h-4 mr-2" />
              Checker
            </TabsTrigger>
            <TabsTrigger 
              value="improve" 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="tab-improve"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Improve
            </TabsTrigger>
            <TabsTrigger 
              value="privacy" 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              data-testid="tab-privacy"
            >
              <Lock className="w-4 h-4 mr-2" />
              Privacy
            </TabsTrigger>
          </TabsList>

          {/* Checker Tab */}
          <TabsContent value="checker" className="space-y-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Input Card */}
              <Card className="border-border bg-card/50 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <Key className="w-6 h-6 text-primary" />
                    Enter Password
                  </CardTitle>
                  <CardDescription>Type or paste your password to analyze</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <Label htmlFor="password-input" className="text-base font-semibold">
                      Password to Analyze
                    </Label>
                    <div className="relative">
                      <Input
                        id="password-input"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password..."
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-14 text-lg pr-12"
                        data-testid="password-input"
                      />
                      <Button
                        onClick={() => setShowPassword(!showPassword)}
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-2"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </Button>
                    </div>
                    
                    {password && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Info className="w-4 h-4" />
                        <span>{password.length} characters</span>
                      </div>
                    )}
                  </div>

                  {analysis && (
                    <div className="space-y-4 pt-4 border-t border-border">
                      <StrengthIndicator />
                    </div>
                  )}

                  <Alert className="bg-primary/10 border-primary/30">
                    <ShieldCheck className="w-4 h-4 text-primary" />
                    <AlertDescription className="text-sm">
                      <strong>Privacy First:</strong> Your password is analyzed entirely in your browser. 
                      It never leaves your device and is never stored.
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>

              {/* Analysis Results */}
              <div className="space-y-6">
                {analysis && (
                  <>
                    {/* Security Metrics */}
                    <Card className="border-border bg-card/50 backdrop-blur-sm">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-xl">
                          <TrendingUp className="w-5 h-5 text-primary" />
                          Security Metrics
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 bg-accent/10 rounded-lg border border-accent/30">
                            <div className="text-sm text-muted-foreground mb-1">Entropy</div>
                            <div className="text-2xl font-bold text-accent">{analysis.entropy} bits</div>
                          </div>
                          
                          <div className="p-4 bg-primary/10 rounded-lg border border-primary/30">
                            <div className="text-sm text-muted-foreground mb-1">Length</div>
                            <div className="text-2xl font-bold text-primary">{analysis.length} chars</div>
                          </div>
                          
                          <div className="col-span-2 p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                            <div className="text-sm text-muted-foreground mb-1">Estimated Crack Time</div>
                            <div className="text-2xl font-bold text-blue-500">{analysis.crackTime}</div>
                            <div className="text-xs text-muted-foreground mt-1">
                              At 10 billion guesses/second
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Requirements Checklist */}
                    <Card className="border-border bg-card/50 backdrop-blur-sm">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-xl">
                          <CheckCircle2 className="w-5 h-5 text-primary" />
                          Security Requirements
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {analysis.requirements.map((req, idx) => (
                            <div
                              key={idx}
                              className={`flex items-center gap-3 p-3 rounded-lg border ${
                                req.met 
                                  ? 'bg-green-500/10 border-green-500/30' 
                                  : 'bg-red-500/10 border-red-500/30'
                              }`}
                            >
                              {req.met ? (
                                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                              ) : (
                                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                              )}
                              <span className={req.met ? 'text-foreground' : 'text-muted-foreground'}>
                                {req.label}
                              </span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Issues Found */}
                    {analysis.issues.length > 0 && (
                      <Card className="border-red-500/30 bg-red-500/10 backdrop-blur-sm">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-xl text-red-500">
                            <AlertTriangle className="w-5 h-5" />
                            Issues Found ({analysis.issues.length})
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {analysis.issues.map((issue, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-red-400">
                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span>{issue}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}

                {!password && (
                  <Card className="border-border bg-card/50 backdrop-blur-sm">
                    <CardContent className="py-16 text-center">
                      <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <p className="text-muted-foreground">
                        Enter a password to see detailed security analysis
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Improve Tab */}
          <TabsContent value="improve" className="space-y-8">
            <div className="max-w-4xl mx-auto space-y-6">
              <Card className="border-border bg-card/50 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl">
                    <Sparkles className="w-6 h-6 text-primary" />
                    Password Improvement Assistant
                  </CardTitle>
                  <CardDescription>
                    Get AI-powered suggestions to strengthen your password
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <Label htmlFor="improve-password" className="text-base font-semibold">
                      Current Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="improve-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter password to improve..."
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-14 text-lg pr-12"
                        data-testid="improve-password-input"
                      />
                      <Button
                        onClick={() => setShowPassword(!showPassword)}
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 top-2"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {analysis && (
                    <>
                      <div className="p-4 bg-accent/10 rounded-lg border border-accent/30">
                        <StrengthIndicator />
                      </div>

                      {analysis.issues.length > 0 && (
                        <div className="space-y-3">
                          <Label className="text-base font-semibold">Recommended Actions</Label>
                          <div className="space-y-2">
                            {analysis.issues.map((issue, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-sm">
                                <Zap className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                <span>{issue}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <Button
                        onClick={generateImprovedPassword}
                        className="w-full h-12 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 cyber-glow"
                        disabled={!password}
                        data-testid="generate-improved-btn"
                      >
                        <Sparkles className="w-5 h-5 mr-2" />
                        Generate Improved Password
                      </Button>

                      {suggestion && (
                        <div className="space-y-4 p-6 bg-primary/10 rounded-xl border-2 border-primary/30">
                          <Label className="text-base font-semibold text-primary">
                            Improved Password Suggestion
                          </Label>
                          <div className="relative">
                            <Textarea
                              value={suggestion}
                              readOnly
                              className="font-mono text-lg min-h-[100px] bg-background"
                              data-testid="improved-password-suggestion"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              onClick={() => copyToClipboard(suggestion)}
                              variant="outline"
                              className="border-primary/50"
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Copy
                            </Button>
                            <Button
                              onClick={useSuggestion}
                              className="bg-primary hover:bg-primary/90"
                            >
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Use This Password
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {!password && (
                    <div className="py-16 text-center">
                      <Sparkles className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <p className="text-muted-foreground">
                        Enter a password above to get improvement suggestions
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-border bg-card/50 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Info className="w-5 h-5 text-primary" />
                    Password Security Tips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold">Use Unique Passwords</div>
                        <div className="text-sm text-muted-foreground">Never reuse passwords across different accounts</div>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold">Enable Two-Factor Authentication</div>
                        <div className="text-sm text-muted-foreground">Add an extra layer of security to your accounts</div>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold">Use a Password Manager</div>
                        <div className="text-sm text-muted-foreground">Let software securely store your passwords</div>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold">Longer is Stronger</div>
                        <div className="text-sm text-muted-foreground">Aim for at least 16 characters for maximum security</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Privacy Tab */}
          <TabsContent value="privacy">
            <Card className="border-border bg-card/50 backdrop-blur-sm max-w-5xl mx-auto">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-3xl">
                  <ShieldCheck className="w-8 h-8 text-primary" />
                  Privacy & Security Guarantee
                </CardTitle>
                <CardDescription className="text-base">
                  Complete transparency about how your passwords are analyzed
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="p-6 rounded-xl bg-primary/10 border-2 border-primary/30">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                        <Lock className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold">100% Local Analysis</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      All password analysis happens entirely in your browser using JavaScript algorithms. 
                      Your passwords never touch any server, API, or database.
                    </p>
                  </div>

                  <div className="p-6 rounded-xl bg-accent/10 border-2 border-accent/30">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                        <ShieldCheck className="w-6 h-6 text-accent" />
                      </div>
                      <h3 className="text-xl font-bold">Zero Storage</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      No passwords are saved to any storage mechanism. The moment you close or refresh 
                      this page, all password data is permanently erased from memory.
                    </p>
                  </div>

                  <div className="p-6 rounded-xl bg-blue-500/10 border-2 border-blue-500/30">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <Zap className="w-6 h-6 text-blue-500" />
                      </div>
                      <h3 className="text-xl font-bold">Real-Time Feedback</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      Advanced algorithms analyze password strength instantly as you type, checking 
                      entropy, patterns, and common password databases locally.
                    </p>
                  </div>

                  <div className="p-6 rounded-xl bg-yellow-500/10 border-2 border-yellow-500/30">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                        <Info className="w-6 h-6 text-yellow-500" />
                      </div>
                      <h3 className="text-xl font-bold">No Tracking</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      Zero analytics, no fingerprinting, no telemetry. We have no idea what passwords 
                      you check or when you use this tool.
                    </p>
                  </div>
                </div>

                <Alert className="bg-primary/10 border-primary/30">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                  <AlertDescription className="text-base">
                    <strong className="text-primary">Privacy Promise:</strong> This tool is designed with privacy-first 
                    principles. All code runs client-side and works offline after initial page load. Your security 
                    is our top priority.
                  </AlertDescription>
                </Alert>

                <div className="space-y-4 p-6 bg-muted/50 rounded-xl border border-border">
                  <h3 className="text-xl font-bold flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    How We Analyze Passwords
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span><strong>Character Set Analysis:</strong> Checks for uppercase, lowercase, numbers, and symbols</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span><strong>Entropy Calculation:</strong> Measures password randomness and unpredictability</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span><strong>Pattern Detection:</strong> Identifies repeated characters and sequential patterns</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span><strong>Common Password Check:</strong> Compares against a local database of known weak passwords</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span><strong>Crack Time Estimation:</strong> Calculates how long it would take to break the password</span>
                    </li>
                  </ul>
                </div>

                <div className="text-center p-8 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border-2 border-primary/30">
                  <ShieldCheck className="w-16 h-16 text-primary mx-auto mb-4 cyber-glow" />
                  <h3 className="text-2xl font-bold mb-2">Your Trust Matters</h3>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    We built this tool to help you create stronger passwords without compromising your privacy. 
                    All analysis happens locally, and we never see or store your passwords.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PasswordStrengthPage;
